import { HttpRequest, HttpResponse, type HttpInterceptorFn } from '@angular/common/http';
import { map } from 'rxjs';

import { deepCamelize, deepSnakeize } from '../../utils/case-mapper';
import { IS_API_REQUEST, SKIP_CASE_MAPPING } from '../api-context';

/**
 * Deep-recursively converts outgoing camelCase → snake_case (request body +
 * query params) and incoming snake_case → camelCase (response body). Mirrors
 * `CaseMapperInterceptor` on the API.
 */
export const caseMapperInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.context.get(IS_API_REQUEST) || req.context.get(SKIP_CASE_MAPPING)) {
    return next(req);
  }

  const mappedRequest = mapRequest(req);

  return next(mappedRequest).pipe(
    map((event) => {
      if (event instanceof HttpResponse) {
        return event.clone({ body: deepCamelize(event.body) });
      }
      return event;
    }),
  );
};

function mapRequest(req: HttpRequest<unknown>): HttpRequest<unknown> {
  let result = req;

  if (req.body !== null && req.body !== undefined) {
    result = result.clone({ body: deepSnakeize(req.body) });
  }

  if (req.params.keys().length > 0) {
    let params = req.params;
    let changed = false;
    for (const key of req.params.keys()) {
      // The expand[] / filter[where][...] etc. params are already snake_case
      // by design; only the top-level keys need conversion. Square brackets
      // remain untouched.
      const snakeKey = snakeizeTopLevelKey(key);
      if (snakeKey === key) continue;
      const values = req.params.getAll(key) ?? [];
      params = params.delete(key);
      for (const value of values) {
        params = params.append(snakeKey, value);
      }
      changed = true;
    }
    if (changed) {
      result = result.clone({ params });
    }
  }

  return result;
}

function snakeizeTopLevelKey(key: string): string {
  const bracketIndex = key.indexOf('[');
  const head = bracketIndex === -1 ? key : key.slice(0, bracketIndex);
  const tail = bracketIndex === -1 ? '' : key.slice(bracketIndex);
  const snakeHead = head.replace(/([A-Z])/g, (_m, letter: string) => '_' + letter.toLowerCase());
  return snakeHead + tail;
}
